// SPDX-License-Identifier: GPL-3.0-or-later
// The music sequencer: a cursor over the sections and a lookahead scheduler.
//
// The caller runs `pump` from the frame loop with the context time. `pump` schedules the sixteenth
// notes that start before `now + LOOKAHEAD_S`. When the frame loop stops, no call comes and
// scheduling stops with it. After a long gap the cursor skips forward. It never schedules a note
// in the past. A state change waits for the next bar line.

import {
  type ActiveMusicState,
  type Arrangement,
  arpNote,
  arrangementFor,
  type Chord,
  type Layers,
  type Section,
  STEPS_PER_BAR,
  stepSeconds,
} from "./music-patterns.ts";
import type { VoiceKit } from "./music-voices.ts";

/** How far ahead of the context time a note is scheduled, in seconds. */
export const LOOKAHEAD_S = 0.16;
/** A step that starts sooner than this after `now` is too late and the cursor skips it. */
export const MIN_LEAD_S = 0.005;
/** Most steps that one skip walks. A longer gap restarts at a bar line. */
const MAX_SKIP_STEPS = 512;

/** The sequencer. */
export interface Sequencer {
  /** Start at a state. The first step plays at context time `t`. */
  start(state: ActiveMusicState, t: number): void;
  /** Ask for a state at the next bar line. */
  queueState(state: ActiveMusicState): void;
  /** The state that plays now (a queued state is not in it yet). */
  state(): ActiveMusicState;
  /** The state that waits for the next bar line, or null. */
  pending(): ActiveMusicState | null;
  /** Seconds in one sixteenth note now. */
  stepSeconds(): number;
  /** Schedule the steps that fall in the lookahead window. */
  pump(kit: VoiceKit, now: number): void;
}

interface Cursor {
  state: ActiveMusicState;
  pending: ActiveMusicState | null;
  step: number;
  bar: number;
  section: number;
  loop: number;
  next: number;
}

function charAt(pattern: string, step: number): string {
  return pattern.charAt(step % pattern.length);
}

function chordOf(section: Section, bar: number): Chord {
  return section.chords[bar % section.chords.length] ?? { root: 45, minor: true };
}

function isLastBar(c: Cursor, section: Section): boolean {
  return c.bar === section.chords.length - 1;
}

/** A fill replaces the last four steps of the last bar on every fourth loop. */
function isFill(c: Cursor, section: Section): boolean {
  return c.loop % 4 === 3 && isLastBar(c, section) && c.step >= 12;
}

function scaled(vel: number, ghost: boolean, scale: number): number {
  return (ghost ? vel * 0.5 : vel) * scale;
}

function scheduleKickSnare(kit: VoiceKit, c: Cursor, s: Section, l: Layers, t: number): void {
  const k = charAt(s.kick, c.step);
  if (l.kick && k !== ".") kit.kick(t, scaled(1, k === "g", l.level));
  if (!l.snare) return;
  if (isFill(c, s)) {
    kit.snare(t, (0.55 + 0.1 * (c.step - 12)) * l.level);
    return;
  }
  const n = charAt(s.snare, c.step);
  if (n !== ".") kit.snare(t, scaled(1, n === "g", l.level));
}

function scheduleHat(kit: VoiceKit, c: Cursor, s: Section, l: Layers, t: number): void {
  if (!l.hat) return;
  const h = charAt(s.hat, c.step);
  const open = h === "o" || (c.loop % 2 === 1 && c.step === 14);
  if (h === "." && !open) return;
  kit.hat(t, scaled(1, h === "g", l.level), open, c.step + c.bar * STEPS_PER_BAR);
}

function scheduleBass(
  kit: VoiceKit,
  c: Cursor,
  s: Section,
  l: Layers,
  t: number,
  stepS: number,
): void {
  if (!l.bass) return;
  const b = charAt(s.bass, c.step);
  if (b === ".") return;
  const chord = chordOf(s, c.bar);
  const offset = b === "o" ? 12 : b === "f" ? 7 : 0;
  const long = charAt(s.bass, c.step + 1) === ".";
  kit.bass(t, chord.root + offset, stepS * (long ? 1.8 : 0.9), l.level);
}

function schedulePad(
  kit: VoiceKit,
  c: Cursor,
  s: Section,
  l: Layers,
  t: number,
  stepS: number,
): void {
  if (!l.pad || c.step !== 0) return;
  const chord = chordOf(s, c.bar);
  const third = chord.minor ? 3 : 4;
  const base = chord.root + 12;
  kit.pad(t, base, base + third, base + 7, stepS * STEPS_PER_BAR * 1.02, l.level);
}

/** The arpeggio index at a step. Some loops rotate the shape by one step. */
function arpIndex(c: Cursor, s: Section): number {
  const rotate = c.loop % 3 === 2 ? 1 : 0;
  return s.arp[(c.step + rotate) % STEPS_PER_BAR] ?? -1;
}

function scheduleArp(
  kit: VoiceKit,
  c: Cursor,
  s: Section,
  l: Layers,
  t: number,
  stepS: number,
): void {
  if (!l.arp) return;
  const index = arpIndex(c, s);
  if (index < 0) return;
  const chord = chordOf(s, c.bar);
  const octave = c.loop % 2 === 1 && isLastBar(c, s) ? 12 : 0;
  if (c.step % l.arpEvery === 0) {
    const midi = arpNote(chord, index) + octave;
    kit.lead(t, midi, stepS * 0.9 * l.arpEvery, l.arpGain * l.level, l.arpWave);
  }
  if (l.sparkle && c.step % 2 === 1) {
    kit.lead(t, arpNote(chord, index) + 12, stepS * 0.5, 0.35 * l.level, "square");
  }
}

function scheduleFanfare(
  kit: VoiceKit,
  c: Cursor,
  s: Section,
  l: Layers,
  t: number,
  stepS: number,
): void {
  if (!l.fanfare || c.bar % 2 !== 0 || (c.step !== 0 && c.step !== 8)) return;
  const chord = chordOf(s, c.bar);
  const midi = chord.root + 36 + (c.step === 0 ? 0 : 7);
  kit.lead(t, midi, stepS * 6, 0.5 * l.level, "sawtooth");
}

function scheduleStep(kit: VoiceKit, c: Cursor, a: Arrangement, t: number): void {
  const section = a.sections[c.section] ?? a.sections[0];
  if (!section) return;
  const stepS = stepSeconds(a.bpm);
  scheduleKickSnare(kit, c, section, a.layers, t);
  scheduleHat(kit, c, section, a.layers, t);
  scheduleBass(kit, c, section, a.layers, t, stepS);
  schedulePad(kit, c, section, a.layers, t, stepS);
  scheduleArp(kit, c, section, a.layers, t, stepS);
  scheduleFanfare(kit, c, section, a.layers, t, stepS);
}

function sequencerArrangement(c: Cursor): Arrangement {
  return arrangementFor(c.state);
}

function sequencerStepS(c: Cursor): number {
  return stepSeconds(sequencerArrangement(c).bpm);
}

/** A queued state starts at the first step of a bar and at the first section. */
function sequencerApplyPending(c: Cursor): void {
  if (!c.pending) return;
  c.state = c.pending;
  c.pending = null;
  c.bar = 0;
  c.section = 0;
}

function sequencerAdvance(c: Cursor): void {
  c.step += 1;
  if (c.step < STEPS_PER_BAR) return;
  c.step = 0;
  c.bar += 1;
  const sections = sequencerArrangement(c).sections;
  if (c.bar < (sections[c.section]?.chords.length ?? 1)) return;
  c.bar = 0;
  c.section += 1;
  if (c.section < sections.length) return;
  c.section = 0;
  c.loop += 1;
}

function sequencerStepOnce(c: Cursor, kit: VoiceKit | null): void {
  if (c.step === 0) sequencerApplyPending(c);
  if (kit) scheduleStep(kit, c, sequencerArrangement(c), c.next);
  c.next += sequencerStepS(c);
  sequencerAdvance(c);
}

/** Walk the cursor to `target` without a sound. A very long gap restarts at a bar line. */
function sequencerSkipTo(c: Cursor, target: number): void {
  for (let i = 0; c.next < target; i++) {
    if (i >= MAX_SKIP_STEPS) {
      c.step = 0;
      sequencerApplyPending(c);
      c.next = target;
      return;
    }
    sequencerStepOnce(c, null);
  }
}

function sequencerStart(c: Cursor, state: ActiveMusicState, t: number): void {
  c.state = state;
  c.pending = null;
  c.step = 0;
  c.bar = 0;
  c.section = 0;
  c.next = t;
}

function sequencerPump(c: Cursor, kit: VoiceKit, now: number): void {
  if (c.next < now + MIN_LEAD_S) sequencerSkipTo(c, now + MIN_LEAD_S);
  const limit = now + LOOKAHEAD_S;
  while (c.next < limit) sequencerStepOnce(c, kit);
}

/** Make a sequencer. */
export function createSequencer(): Sequencer {
  const c: Cursor = {
    state: "attract",
    pending: null,
    step: 0,
    bar: 0,
    section: 0,
    loop: 0,
    next: 0,
  };
  return {
    start: (state, t) => sequencerStart(c, state, t),
    queueState(state) {
      c.pending = state === c.state ? null : state;
    },
    state: () => c.state,
    pending: () => c.pending,
    stepSeconds: () => sequencerStepS(c),
    pump: (kit, now) => sequencerPump(c, kit, now),
  };
}
